import {useMemo} from "react";
import {usePage} from "@inertiajs/react";
import type {CatalogDrawerFilter} from "../model/types";
import {useCatalogFilterDrawer} from "../model/useCatalogFilterDrawer";
import {type CatalogSortDto, CatalogSortOptionsFilter, mapCatalogSortOptions} from "@/features/catalog-sort";
import {CatalogOptionsFilter, CatalogRangeFilter} from "@/features/catalog-filters";
import {catalogEvents, useSubscribeEvents} from "@/shared/lib/events";
import {declOfNum} from "@/shared/lib/number/declOfNum";
import {useStoreMobx} from "@/shared/lib/mobx/useStoreMobx"; // Legacy
import {Svg} from "@/shared/ui/Svg";
import {Button} from "@/shared/ui/Button";
import {Drawer} from "@/shared/ui/Drawer";
import styles from "./CatalogFilterDrawer.module.scss";
import {observer} from "mobx-react"; // Legacy

type PageProps = {
    filters: CatalogDrawerFilter[];
    dataSort: CatalogSortDto[];
    insertColors: Record<string, string>
};

const OPEN_FILTER_EVENTS = [catalogEvents.openCatalogFilter];
const CLOSE_FILTER_EVENTS = [catalogEvents.closeCatalogFilter];

export const CatalogFilterDrawer = observer(() => {
    const {filters = [], dataSort = [], insertColors = {}} = usePage<PageProps>().props;

    const opened = useCatalogFilterDrawer(s => s.opened);
    const filterKey = useCatalogFilterDrawer(s => s.filterKey);
    const openModal = useCatalogFilterDrawer(s => s.actions.openModal);
    const closeModal = useCatalogFilterDrawer(s => s.actions.closeModal);

    const {catalogStore} = useStoreMobx();

    useSubscribeEvents(OPEN_FILTER_EVENTS, ({filterKey}) => openModal(filterKey));
    useSubscribeEvents(CLOSE_FILTER_EVENTS, closeModal);

    const filter = useMemo(() => {
        if (!filterKey) {
            return null;
        }
        if (filterKey === "sorting") {
            const options = mapCatalogSortOptions({
                options: dataSort,
            });
            return {type: "sort" as const, title: "Сортировка", options};
        }
        return filters.find((filter) => filter.key === filterKey);
    }, [filters, dataSort, filterKey]);

    const showProducts = () => {
        catalogEvents.closeCatalogFilters.emit();
        closeModal();
    }

    if (!filter) return null;

    const renderContent = () => {
        switch (filter.type) {
            case "sort":
                return (
                    <CatalogSortOptionsFilter
                        options={filter.options}
                        selectedValue={catalogStore.selectedSortingValue}
                        checkboxContainerClassName={styles.labelContainer}
                        textPosition={`left`}
                        onChange={(option) => {
                            catalogStore.applySorting(option);
                            closeModal();
                        }}
                    />
                );
            case "range":
                return (
                    <CatalogRangeFilter
                        option={filter.options}
                        onChange={(values) => {
                            catalogStore.onBlur(values, filter.options);
                        }}
                    />
                );
            default:
                return (
                    <CatalogOptionsFilter
                        options={filter.options}
                        showAll
                        insertColors={insertColors}
                        className={styles.drawerOptionsList}
                        checkboxContainerClassName={styles.labelContainer}
                        textPosition={`left`}
                        onChange={(option) => {
                            catalogStore.onChangeFilter(
                                option,
                                filter.dependencies,
                            );
                        }}
                    />
                );
        }
    };

    return (
        <Drawer
            anchor={`right`}
            open={opened}
            onClose={closeModal}
            classes={{
                root: styles.catalogFilterDrawer,
                paper: styles.drawerPaper,
            }}
            sx={{
                zIndex: "calc(var(--base-drawer-z-index) + 1000)",
            }}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <button type={`button`} className={styles.backButton} onClick={closeModal}>
                        <Svg id={`arrowDown`} className={styles.arrowIcon} />
                    </button>
                    <p className={styles.filterName}>{filter.title}</p>
                </div>
                <div className={styles.body}>
                    {renderContent()}
                </div>
                <div className={styles.footer}>
                    <Button fullWidth size={`xl`} onClick={showProducts}>
                        {catalogStore.catalogCount > 0 ?
                            <>
                                Показать {catalogStore.catalogCount.toLocaleString()} {declOfNum(catalogStore.catalogCount, [' товар', ' товара', ' товаров'])}
                            </>
                            : 'Товары не найдены'
                        }
                    </Button>
                </div>
            </div>
        </Drawer>
    );
});
